import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import watchesData from '../../data/watches.json';
import variantImagesData from '../../data/variantImages.json';
import Title from '../../components/Title/Title';
import './VariantDetail.css';
import { useLanguage } from '../../context/LanguageContext';

interface WatchVariant {
    id: string;
    imageSrc: string;
    model: string;
    code: string;
    movement: string;
    description: {
        [key: string]: string;  // This allows indexing with language
    };
    subtitle?: string;
    serialNumber?: string;
    case: string;
    dial: string;
    indexGlass: string;
    bracelet: string;
    gender: 'gents' | 'ladies';
}

interface TabProps {
    label: string;
    isActive: boolean;
    onClick: () => void;
}

interface VariantImage {
    src: string;
    view: string;
}

interface VariantImagesMap {
    [key: string]: {
        front?: string;
        angle?: string;
        side?: string;
        back?: string;
    };
}

interface AppointmentPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

const translations = {
    en: {
        bookAppointment: "Book Your Appointment",
        visitStore: "Visit a Store",
        visitStoreDesc: "Experience our collection in person at our nearest boutique",
        findStore: "Find Nearest Store",
        callUs: "Call Us",
        callUsDesc: "Speak with our customer service representative",
        priceOnRequest: "PRICE ON REQUEST",
        bookAppointmentButton: "Book Your Appointment",
        variantNotFound: "Variant not found",
        serialNumber: "Serialnumber",
        // Tab labels
        case: "CASE",
        dial: "DIAL",
        indexGlass: "INDEX GLASS",
        bracelet: "BRACELET",
        // Specification labels
        material: "MATERIAL",
        movement: "MOVEMENT"
    },
    de: {
        bookAppointment: "Termin Vereinbaren",
        visitStore: "Besuchen Sie eine Boutique",
        visitStoreDesc: "Erleben Sie unsere Kollektion persönlich in unserer nächstgelegenen Boutique",
        findStore: "Nächste Boutique Finden",
        callUs: "Rufen Sie Uns An",
        callUsDesc: "Sprechen Sie mit unserem Kundenservice",
        priceOnRequest: "PREIS AUF ANFRAGE",
        bookAppointmentButton: "Termin Vereinbaren",
        variantNotFound: "Variante nicht gefunden",
        serialNumber: "Seriennummer",
        // Tab labels
        case: "GEHÄUSE",
        dial: "ZIFFERBLATT",
        indexGlass: "INDEXGLAS",
        bracelet: "ARMBAND",
        // Specification labels
        material: "MATERIAL",
        movement: "UHRWERK"
    }
};

const AppointmentPopup: React.FC<AppointmentPopupProps> = ({ isOpen, onClose }) => {
    const { language } = useLanguage();
    const t = translations[language];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-8 max-w-md w-full m-4 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-black"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <h2 className="text-2xl font-light mb-6">{t.bookAppointment}</h2>

                <div className="space-y-6">
                    <div className="p-6 border border-gray-200 rounded-lg hover:border-black transition-colors cursor-pointer">
                        <h3 className="text-lg font-medium mb-2">{t.visitStore}</h3>
                        <p className="text-gray-600 mb-4">{t.visitStoreDesc}</p>
                        <button onClick={() => window.location.href = '/stores/stores'} className="text-black underline">
                            {t.findStore}
                        </button>
                    </div>

                    <div className="p-6 border border-gray-200 rounded-lg hover:border-black transition-colors cursor-pointer">
                        <h3 className="text-lg font-medium mb-2">{t.callUs}</h3>
                        <p className="text-gray-600 mb-4">{t.callUsDesc}</p>
                        <a href="tel:+972586398333" className="text-black underline">+(972) 58-639-8333</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Tab: React.FC<TabProps> = ({ label, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`relative uppercase text-sm tracking-wider px-4 py-2 ${isActive ? 'font-medium text-black' : 'text-gray-500'
            }`}
    >
        {label}
        {isActive && (
            <div className="absolute bottom-0 left-0 w-full h-[2px] bg-black" />
        )}
    </button>
);

function VariantDetail() {
    const { collectionId, variantId } = useParams();
    const { language } = useLanguage();
    const t = translations[language];
    const [activeTab, setActiveTab] = useState(t.case);
    const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
    const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
    const [isAppointmentPopupOpen, setIsAppointmentPopupOpen] = useState(false);

    // Find the variant data
    const collection = watchesData[collectionId as keyof typeof watchesData] || {};
    const variant = Object.values(collection)
        .flat()
        .find(item => item.code === variantId) as WatchVariant | undefined;

    // Get variant images from the mapping
    const variantImages: VariantImage[] = [];
    if (variant) {
        const variantImageData = (variantImagesData as VariantImagesMap)[variant.code];
        if (variantImageData) {
            if (variantImageData.front) {
                variantImages.push({
                    src: variantImageData.front,
                    view: 'Front View'
                });
            }
            if (variantImageData.angle) {
                variantImages.push({
                    src: variantImageData.angle,
                    view: 'Angle View'
                });
            }
            if (variantImageData.side) {
                variantImages.push({
                    src: variantImageData.side,
                    view: 'Side View'
                });
            }
            if (variantImageData.back) {
                variantImages.push({
                    src: variantImageData.back,
                    view: 'Back View'
                });
            }
        }
    }

    const handleImageLoad = (src: string) => {
        setLoadedImages(prev => new Set(Array.from(prev).concat(src)));
    };

    const handleImageError = (src: string) => {
        setFailedImages(prev => new Set(Array.from(prev).concat(src)));
    };

    if (!variant) {
        return <div>{t.variantNotFound}</div>;
    }

    const carouselResponsive = {
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: 1
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 1
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1
        }
    };

    const tabs = [t.case, t.dial, t.indexGlass, t.bracelet];

    const renderTabContent = () => {
        switch (activeTab) {
            case t.case:
                return (
                    <div className="space-y-4">
                        <div>
                            <h3 className="font-medium text-black mb-2">{t.material}</h3>
                            <p className="text-gray-600">{variant.case}</p>
                        </div>
                        <div>
                            <h3 className="font-medium text-black mb-2">{t.movement}</h3>
                            <p className="text-gray-600">{variant.movement}</p>
                        </div>
                    </div>
                );
            case t.dial:
                return <p className="text-gray-600">{variant.dial}</p>;
            case t.indexGlass:
                return <p className="text-gray-600">{variant.indexGlass}</p>;
            case t.bracelet:
                return <p className="text-gray-600">{variant.bracelet}</p>;
            default:
                return null;
        }
    };

    const renderImages = () => {

        return (
            <div className="relative aspect-square">
                <img
                    src={`/images/${variant.imageSrc}.png`}
                    alt={`${variant.model}`}
                    className="w-full h-full object-contain"
                    onLoad={() => handleImageLoad(variant.imageSrc)}
                    onError={() => handleImageError(variant.imageSrc)}
                />
            </div>
        );

        // If we have variant images, show them in a carousel. Uncomment once images are available
        // return (
        //     <Carousel
        //         responsive={carouselResponsive}
        //         infinite={true}
        //         showDots={false}
        //         removeArrowOnDeviceType={["tablet", "mobile"]}
        //         className="product-carousel"
        //     >
        //         {variantImages
        //             .filter(image => !failedImages.has(image.src))
        //             .map((image, index) => (
        //                 <div key={index} className="relative aspect-square">
        //                     <img
        //                         src={`/images/watch_variants/${image.src}`}
        //                         alt={`${variant.model} - ${image.view}`}
        //                         className="w-full h-full object-contain"
        //                         onLoad={() => handleImageLoad(image.src)}
        //                         onError={() => handleImageError(image.src)}
        //                     />
        //                 </div>
        //             ))}
        //     </Carousel>
        // );
    };

    return (
        <div className="min-h-screen bg-white">
            <AppointmentPopup
                isOpen={isAppointmentPopupOpen}
                onClose={() => setIsAppointmentPopupOpen(false)}
            />
            <div className="mx-auto px-24 mt-[150px]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Left Column - Details */}
                    <div className="space-y-8">
                        <Title
                            firstLine={collectionId?.toUpperCase()}
                            secondLine={variant?.gender.toUpperCase() || ''}
                            className='mb-4'
                        />

                        {variant.serialNumber && (
                            <div>
                                <p className="text-sm text-gray-600">
                                    {t.serialNumber} <span className="font-medium">{variant.serialNumber}</span>
                                </p>
                            </div>
                        )}

                        <div className="text-gray-600">
                            {variant.description[language]}
                        </div>

                        <div>
                            <div className="flex border-b border-gray-200">
                                {tabs.filter(tab => tab !== 'DESCRIPTION').map(tab => (
                                    <Tab
                                        key={tab}
                                        label={tab}
                                        isActive={activeTab === tab}
                                        onClick={() => setActiveTab(tab)}
                                    />
                                ))}
                            </div>
                            <div className="mt-6 h-[130px] overflow-y-auto custom-scrollbar">
                                {renderTabContent()}
                            </div>
                        </div>

                        <div className="pt-8">
                            <p className="text-sm text-gray-600 italic">{t.priceOnRequest}</p>
                            <button
                                onClick={() => setIsAppointmentPopupOpen(true)}
                                className="mt-4 w-full bg-black text-white py-3 px-6 uppercase text-sm tracking-wider hover:bg-gray-800 transition-colors"
                            >
                                {t.bookAppointmentButton}
                            </button>
                        </div>
                    </div>

                    {/* Right Column - Image */}
                    <div className="lg:pl-12">
                        {renderImages()}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VariantDetail; 